/**
 * 작성자 : 박승호
 * 날짜 : 2026.08.04
 * 경로 : 센서 관리 - 소음 측정 이력
 */
import { useEffect, useState, useRef, RefObject } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { pageInfoState, userState } from '../../../atoms';
import { BtnGhost, BtnGray } from '../../../components/Button';
import { LIST_COUNT, SENSOR_TIME, SOUND_UNIT } from '../../../_constants';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { scrollToNodeTop } from '../../../utils/scrollToNodeTop';
import { useDetectScrolledToBottom } from '../../../utils/useDetectScrolledToBottom';
import { IoChevronUpSharp } from 'react-icons/io5';
import { FloatingButtonToTop } from '../../../assets/styles/FloatingButtonToTop';
import Tab from '../../../components/Tab';
import { IComCdList, ITabObject } from 'customTypes';
import { setComCdListState } from '../../../utils/setComCdListState';
import { todayYYYYMMDD, toMonthYYYMMDD } from '../../../utils/formatDate';
import { useReactToPrint } from 'react-to-print';
import Portal from '../../../components/Portal';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import dayjs from 'dayjs';
import LineChartModal from '../../../components/Modal/lineChartModal';
import useOnKeydownF9 from '../../../utils/useOnKeydownF9';
import { useSetAuth } from '../../../utils/useSetAuth';
import TuiGrid from '../../../components/Table/TuiGrid';
import { logPost } from '../../../services/log';
import SelectBox from '../../../components/SelectBox';
import illustrator from '../../../assets/images/illustration/31.svg';
import { useTranslation } from 'react-i18next';
import i18n from '../../../translation/i18n';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import { useGetFrozenCount } from '../../../utils/getFrozenCount';
import BackButton from '../../../components/BackButton';
import useSetListCount from '../../../utils/useSetListCount';
import nodataIllustrator from '../../../assets/images/illustration/304.svg';
import { apiGet } from '../../../services/_common';
import ShortcutButton from '../../../components/button/ShortcutButton';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import { showExcelButton } from '../../../utils/deviceAuth';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const Root = styled.div`
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .tui-container {
    flex-grow: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }
  .emptyData {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    user-select: none;
    padding-bottom: 4rem;
    img {
      width: 16rem;
      /* user-drag: none; */
      -webkit-user-drag: none;
    }
    span {
      color: var(--filled-violet);
    }
  }
  .end {
    justify-content: end;
    > button {
      margin-left: 1rem;
    }
  }
  .mgas-responsive {
    flex-wrap: nowrap;
    @media (min-width: 1025px) {
      flex-wrap: wrap;
      overflow: visible !important;
    }
  }
`;
const HistorySound = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const INIT_F_NAME = { type: 'fName', fName: 'A', cdName: t('전체') };
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const tabInfo = useRecoilValue(pageInfoState);
  const toDay = todayYYYYMMDD();
  const toMonth = toMonthYYYMMDD();
  const date = new Date();
  const userInfo = useRecoilValue(userState);
  const { userMenuList } = userInfo;
  const [rowState, setRowState] = useState<any>({});
  const [viewRowState, setViewRowState] = useState<boolean>(false);
  const [tableState, setTableState] = useState<any[]>([]);
  const [filteredTableState, setFilteredTableState] = useState<any[]>([]);
  const [orgTableState2, setOrgTableState2] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const size = useOutletContext<any>();
  const { defaultMrCd } = useRecoilValue(pageInfoState);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(defaultMrCd);
  const tableRef = useRef<HTMLDivElement>(null);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [rangeState, setRangeState] = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [fNameList, setFnameList] = useState<any[]>([]);
  const [searchOption, setSearchOption] = useState({ start: toDay, end: toDay });
  const [fName, setFName] = useState(INIT_F_NAME);
  const [soundUnit, setSoundUnit] = useState({ type: 'soundUnit', soundUnit: 'A', cdName: t('전체') });
  const [sensorTime, setSensorTime] = useState<any>({ type: 'time', time: '1', cdName: t('시간') });
  const [excelBtn, setExcelBtn] = useState(false);
  const [chartData, setChartData] = useState<any>([]);
  const [chartData2, setChartData2] = useState<any>([]);
  const [datasets] = useState<any>([
    {
      label: t('소음'),
      data: 'sound',
      borderColor: 'rgba(0, 221, 255, 0.5)',
      backgroundColor: 'rgba(0, 221, 255, 0.5)',
    },
    {
      label: t('1분 등가소음'),
      data: 'leq1Min',
      borderColor: 'rgba(0, 119, 255, 0.5)',
      backgroundColor: 'rgba(0, 119, 255, 0.5)',
    },
    {
      label: t('5분 등가소음'),
      data: 'leq5Min',
      borderColor: 'rgba(0, 0, 255, 0.5)',
      backgroundColor: 'rgba(0, 0, 255, 0.5)',
    },
  ]);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '' });
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const showExcelBtn = showExcelButton(auth.excelAuth);

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    if (!rangeState[0].startDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].startDate]);

  useEffect(() => {
    if (!rangeState[0].endDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].endDate]);

  useEffect(() => {
    const setSoundUnitColumns = [
      {
        header: t('순간소음(dB(A))'),
        name: 'sound',
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
      {
        header: t('1분 등가소음(dB(A))'),
        name: 'leq1Min',
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
      {
        header: t('5분 등가소음(dB(A))'),
        name: 'leq5Min',
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
    ];
    const visibleSoundUnitColumns = soundUnit.soundUnit === 'A' ? setSoundUnitColumns : setSoundUnitColumns.filter((col: any) => col.name === soundUnit.soundUnit);

    setColumns([
      {
        header: t('장비명'),
        name: 'dvName',
        sortable: true,
        filter: 'select',
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        // filter: 'select',
        minWidth: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      ...visibleSoundUnitColumns.map(({ unit, ...col }: any) => col),
    ]);
  }, [i18n.language, soundUnit.soundUnit]);

  useEffect(() => {
    setLoading(true);
    getSensorAPI().then((res: any) => {
      if (res.status === 200) {
        getNormalCdAPI();
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '소음 측정 이력',
          action: '조회',
          etc: ``,
        });
      }
    });
  }, []);

  useEffect(() => {
    if (Object.keys(rowState).length !== 0) getSensorDayAPI(rowState);
  }, [rowState, sensorTime]);

  // 센서 리스트 조회 API
  const getSensorAPI = async (start: string = searchOption.start, end: string = searchOption.end) => {
    // const { start, end } = searchOption;
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, dvNo: fName.fName === 'A' ? '' : fName.fName, date1: start.replaceAll('-', ''), date2: end.replaceAll('-', '') };
    const res = await apiGet({ path: '/sensor/soundRaw', req });
    const { data, statusCode } = res.data !== undefined && res.data;
    if (statusCode === 200) {
      setLoading(false);
      setTableState(data);
      const dvList = toDvList(data);
      const newData = dvList.map((v: any) => {
        return data.filter((v2: any) => v2.dvNo === v.dvNo);
      });
      const reverseData = newData.map((innerArray: any) => [...innerArray].reverse());
      setChartData(reverseData);
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
    return res;
  };

  // 장비 리스트 조회 API
  const getNormalCdAPI = async () => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, grCd: '02' };
    const res = await apiGet({ path: '/sensor/detail', req });
    const { data, statusCode } = res.data !== undefined && res.data;
    if (statusCode === 200) {
      const newList = [];
      newList.push(INIT_F_NAME);
      data.sensorList.map((v: any) => {
        newList.push({ type: 'fName', fName: v.subCd, cdName: v.sName });
      });
      setFnameList(newList);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 센서 일별 조회 API
  const getSensorDayAPI = async (rowData: any) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, dvNo: rowData.dvNo, date: rowData.checkTime?.replaceAll('-', ''), time: sensorTime.time };
    const res = await apiGet({ path: '/sensor/soundDay', req });
    const { data, statusCode } = res.data !== undefined && res.data;
    if (statusCode === 200) {
      const reverseData = [data].map((innerArray: any) => [...innerArray].reverse());
      if (sensorTime.time === '1') {
        setOrgTableState2(data);
        setChartData2(reverseData);
      } else {
        setChartData2(reverseData);
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  const toDvList = (data: any[]) => {
    const map = new Map(data.map((m: any) => [m.dvNo, m])).entries();
    return Array.from(map).map((v: any) => {
      return { dvNo: v[1].dvNo, dvName: v[1].dvName };
    });
  };

  // 검색 버튼 클릭
  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    if (!openModal.status || (openModal.status && openModal.type === 'filter')) {
      if (searchOption.end && searchOption.start > searchOption.end) {
        toast.warning(t('날짜를 다시 입력하세요'));
        setSearchOption((prev: any) => ({ ...prev, end: '' }));
      } else if (
        searchOption.start === '' ||
        searchOption.end === '' ||
        (searchOption.start === '' && searchOption.end === '') ||
        (searchOption.start && searchOption.end && dayjs(searchOption.end).diff(dayjs(searchOption.start), 'month') > 3)
      ) {
        // 시작|종료|시작과 종료기간이 없거나(전체기간) 시작기간과 종료기간 차이가 3개월 초과 시
        setLoading(true);
        toast.info(t('기간은 최대 3개월 단위로 조회됩니다.'));
        if (filterRef) {
          // 모바일일 경우 검색 필터 모달 창 닫기
          filterRef.current?.classList.remove('active');
          setOpenModal({ status: false, type: 'filter' });
        }
        setRowState({});
        setVisibleRangePicker(false);
        navigate({ pathname: location.pathname, search: '' });

        const isAllDateEmpty = searchOption.start === '' && searchOption.end === '';
        const isEndDateEmpty = searchOption.end === '';

        // 두 기간이 모두 없을 경우(전체기간) 오늘로부터 3개월 전 기간으로 설정
        const startDate = isAllDateEmpty
          ? dayjs(toDay).subtract(3, 'month').format('YYYY-MM-DD')
          : isEndDateEmpty
          ? searchOption.start
          : dayjs(searchOption.end).subtract(3, 'month').format('YYYY-MM-DD'); // 종료기간이 있을 경우 종료기간으로부터 3개월 전 기간으로 설정
        const endDate = isAllDateEmpty
          ? dayjs(toDay).format('YYYY-MM-DD')
          : isEndDateEmpty
          ? dayjs(searchOption.start).add(3, 'month').isAfter(dayjs(toDay)) // 시작시간이 오늘보다 클 경우 오늘까지 설정
            ? dayjs(toDay).format('YYYY-MM-DD')
            : dayjs(searchOption.start).add(3, 'month').format('YYYY-MM-DD') // 종료기간이 없을 경우 시작기간으로부터 3개월 후 기간으로 설정
          : searchOption.end; // 종료기간이 있을 경우 종료기간으로 설정

        // 설정한 기간으로 변경
        setSearchOption({ start: startDate, end: endDate });
        setRangeState([{ startDate: dayjs(startDate).toDate(), endDate: dayjs(endDate).toDate(), key: 'selection' }]);

        getSensorAPI(startDate, endDate);
      } else {
        setLoading(true);
        if (filterRef) {
          // 모바일일 경우 검색 필터 모달 창 닫기
          filterRef.current?.classList.remove('active');
          setOpenModal({ status: false, type: 'filter' });
        }
        setRowState({});
        setVisibleRangePicker(false);
        navigate({ pathname: location.pathname, search: '' });
        getSensorAPI();
      }
    }
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev: any) => ({ ...prev, [el]: '', start: toDay, end: toDay }));
    });
    setFName(INIT_F_NAME);
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  };

  // 차트 버튼 클릭
  const onClickChart = () => {
    if (tableState.length !== 0) {
      setOpenModal({ status: true, type: 'lineChart' });
    } else {
      toast.warning(t('데이터가 없습니다'));
    }
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = async () => {
    if (tableState.length <= 0) return;
    // setExcelBtn(true);

    const exportRows = filteredTableState.length > 0 ? filteredTableState : tableState;

    // soundUnit이 '전체(A)'면 3개 지표 모두, 아니면 선택된 지표 컬럼만 노출
    const soundColumnDefs = [
      { unit: 'sound', header: t('순간소음(dB(A))'), key: 'sound', width: 20, style: { alignment: { horizontal: 'right' } } },
      { unit: 'leq1Min', header: t('1분 등가소음(dB(A))'), key: 'leq1Min', width: 20, style: { alignment: { horizontal: 'right' } } },
      { unit: 'leq5Min', header: t('5분 등가소음(dB(A))'), key: 'leq5Min', width: 20, style: { alignment: { horizontal: 'right' } } },
    ];
    const visibleSoundColumns = soundUnit.soundUnit === 'A' ? soundColumnDefs : soundColumnDefs.filter((col) => col.unit === soundUnit.soundUnit);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('소음 측정 이력'); // 소음 측정 이력 탭

    worksheet.views = [{ state: 'frozen', ySplit: 1, topLeftCell: 'A2' }];

    worksheet.columns = [
      { header: 'No.', key: 'no', width: 10, style: { alignment: { horizontal: 'center' } } },
      { header: '장비명', key: 'dvName', width: 12, style: { alignment: { horizontal: 'center' } } },
      { header: '측정일자', key: 'checkTime', width: 20, style: { alignment: { horizontal: 'center' } } },
      ...visibleSoundColumns.map(({ unit, ...col }: any) => col),
    ];

    worksheet.getRow(1).height = 24;
    worksheet.getRow(1).eachCell((cell: any, cellIdx: number) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
      // cell이 마지막인 경우만 오른쪽 테두리 굵음
      if (cellIdx === worksheet.columns.length) {
        cell.border = { right: { style: 'medium' }, bottom: { style: 'medium' } };
      } else {
        cell.border = { right: { style: 'thin' }, bottom: { style: 'medium' } };
      }
      cell.font = { name: '맑은 고딕', size: 11, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    exportRows.forEach((v: any, index: number) => {
      const row: any = { no: index + 1, dvName: v.dvName, checkTime: v.checkTime };
      visibleSoundColumns.forEach((col) => {
        row[col.key] = Number(v[col.key]);
      });
      worksheet.addRow(row);
      worksheet.getRow(index + 2).eachCell((cell: any, cellIdx: number) => {
        if (cellIdx === worksheet.columns.length) {
          cell.border = { right: { style: 'medium' }, bottom: { style: 'thin' } };
        } else {
          cell.border = { right: { style: 'thin' }, bottom: { style: 'thin' } };
        }
        cell.alignment = { vertical: 'center', horizontal: 'center' };
      });
      // 마지막 row는 아래 테두리만 굵음
      if (index === exportRows.length - 1) {
        worksheet.getRow(index + 2).eachCell((cell: any, cellIdx: number) => {
          if (cellIdx === worksheet.columns.length) {
            cell.border = { right: { style: 'medium' }, bottom: { style: 'medium' } };
          } else {
            cell.border = { right: { style: 'thin' }, bottom: { style: 'medium' } };
          }
        });
      }
    });

    // 5. 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${searchOption.start}~${searchOption.end}-소음 측정 이력.xlsx`);

    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '소음 측정 이력',
      action: '엑셀 저장',
      etc: `${searchOption.start}~${searchOption.end}`,
    });
  };

  const handlePrint = useReactToPrint({ content: () => componentRef.current });
  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };

  // 측정기 Row 클릭
  const onClickRow = async (rowData: any) => {
    if (rowData) {
      setRowState(rowData);
      setViewRowState(true);
    }
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  return (
    <div className='content-container oneColumn'>
      <Root>
        <SearchOptions>
          {size.innerSize.W > 1024 ? (
            <>
              {isBottom && (
                <FloatingButtonToTop>
                  <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                    <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
                  </button>
                </FloatingButtonToTop>
              )}
              <div className='inputsWrapper'>
                <div className='inputForm-col withLabelComCf'>
                  <label htmlFor='wForeignyn'>{t('장비')}</label>
                  <SelectBox
                    options={fNameList}
                    defaultOption={t('전체')}
                    state={fName}
                    setState={setFName}
                    stateKey='fName'
                    filterbar='filter-1-left'
                    initiateKey={fName.fName}
                    optionHeight='height-md'
                  />
                </div>
                <div className='inputForm-col withLabelComCf'>
                  <label htmlFor='soundUnit'>{t('측정 항목')}</label>
                  <SelectBox
                    options={SOUND_UNIT}
                    defaultOption={t('전체')}
                    state={soundUnit}
                    setState={setSoundUnit}
                    stateKey='soundUnit'
                    initiateKey={soundUnit.soundUnit}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
                <div className='inputForm-row-fit'>
                  <span className='calendarLabel'>{t('조회일자')}</span>
                  <div className='inputForm-col'>
                    <div className='flex-basic'>
                      <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                      <span className='inputDash'> ~ </span>
                      <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                    </div>
                    {visibleRangePicker && (
                      <div className='rangePickerWrapper'>
                        <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                      </div>
                    )}
                  </div>
                  <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                    <span className='material-symbols-rounded'>calendar_month</span>
                  </button>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='secondSearchOption'>
                  <div className='flex-basic textBtnGroup'>
                    <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                    <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                    <div className='searchResult'>
                      {t('총')}
                      <span>{tableState.length}</span>
                      {t('개')}
                    </div>
                    <div className='inputForm-row'>
                      <div className='inputForm-col withLabelComCf'>
                        <label htmlFor='useYn'>{t('보기 설정')}</label>
                        <SelectBox
                          options={listCountComCdList}
                          defaultOption={listCount.cdName}
                          state={listCount}
                          setState={setListCount}
                          stateKey={LIST_COUNT}
                          initiateKey={listCount[LIST_COUNT]}
                          setTableLinesAPI={patchUserMenuAPI}
                          optionHeight='height-sm'
                          rsearch
                        />
                      </div>
                    </div>
                  </div>
                  <div className='flex-basic iconBtnGroup'>
                    {/* <BtnGhost onClick={onClickChart}>
                      <span className='material-symbols-rounded'>monitoring</span>
                      {t('차트')}
                    </BtnGhost> */}
                    {renderExcelBtn()}
                    {/* {renderPrintBtn()} */}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='filter-wrapper'>
              <div className='inputsWrapper'>
                <div className='flex-basic iconBtnGroup'>
                  <BtnGhost onClick={onClickFilter} className='filter'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                    {t('검색 필터')}
                  </BtnGhost>
                  {/* <BtnGhost onClick={onClickChart}>
                    <span className='material-symbols-rounded'>monitoring</span>
                    {t('차트')}
                  </BtnGhost> */}
                  {renderExcelBtn()}
                </div>
              </div>
            </div>
          )}
        </SearchOptions>
        {tableState.length > 0 ? (
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={tableState}
              filterTableState={filteredTableState}
              setFilterTableState={setFilteredTableState}
              columns={columns}
              perPage={Number(listCount[LIST_COUNT])}
              excelBtn={excelBtn}
              usePagenation
              onClickRow={onClickRow}
              scrollX
              height={tuiHeight}
              // frozenCount={0}
              pageNum={1}
              gridId='grid1'
            />
          </TuiGridWrapper>
        ) : (
          <div className='emptyData'>
            <img src={nodataIllustrator} alt='noData' />
            <span>{t('해당 일자의 데이터가 존재하지 않습니다')}</span>
          </div>
        )}
      </Root>

      <Portal openModal={loading}>
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
        </LoadingModalBackground>
      </Portal>
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'lineChart' && <LineChartModal setOpenModal={setOpenModal} chartData={chartData} datasets={datasets} title={t('소음 측정기')} />}
      </Portal>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('조회일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='fName'>{t('장비')}</label>
                  <SelectBox
                    options={fNameList}
                    defaultOption={t('전체')}
                    state={fName}
                    setState={setFName}
                    stateKey='fName'
                    filterbar='filter-1-left'
                    initiateKey={fName.fName}
                    optionHeight='height-md'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='soundUnit'>{t('측정 항목')}</label>
                  <SelectBox
                    options={SOUND_UNIT}
                    defaultOption={t('전체')}
                    state={soundUnit}
                    setState={setSoundUnit}
                    stateKey='soundUnit'
                    filterbar='filter-1-left'
                    initiateKey={soundUnit.soundUnit}
                    optionHeight='height-md'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default HistorySound;
